import React, { useEffect, useMemo, useRef, useState, useLayoutEffect, useCallback } from 'react';

import {
  Box,
  Card,
  Group,
  LoadingOverlay,
  Select,
  MultiSelect,
  SimpleGrid,
  Stack,
  Text,
  Switch,
  useMantineTheme,
  useComputedColorScheme,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Tooltip,
} from 'recharts';

import './PolarPanel.css';
import './themeInversion.css';

// Tamaños mínimos para un layout profesional
const MIN_PANEL_WIDTH = 420;
const MIN_PANEL_HEIGHT = 360;
const MIN_CHART_WIDTH = 380;
const MIN_CHART_HEIGHT = 300;

// Debug helpers
const DEBUG_POLAR = true; // set to false to silence
const TEXT_CONTRAST_COLOR = 'var(--textContrastHigh, var(--contrast))';
const TEXT_MUTED_COLOR = 'var(--textMutedStrong, var(--textMuted))';
const BORDER_SUBTLE_COLOR = 'var(--borderSubtle)';
const SELECT_FIELD_STYLES = {
  label: {
    color: TEXT_CONTRAST_COLOR,
    fontWeight: 600,
    letterSpacing: '0.01em',
  },
  input: {
    color: TEXT_CONTRAST_COLOR,
  },
};

const MULTI_SELECT_FIELD_STYLES = {
  ...SELECT_FIELD_STYLES,
  searchInput: {
    color: TEXT_CONTRAST_COLOR,
  },
};
function dbg(...args: any[]) {
  if (DEBUG_POLAR) {
    // eslint-disable-next-line no-console
    console.log('[PolarPanel]', ...args);
  }
}

/** Quick preview utility for arrays */
function preview<T extends object>(arr: T[], take = 5) {
  if (!Array.isArray(arr)) return arr;
  return arr.slice(0, take);
}

/* =========================== Tipos base =========================== */
export interface TrackPoint {
  utc: string;
  lat: number;
  lon: number;
  twd: number;
  vmg?: number | null;
  bsppc?: number | null;
  vmgpc?: number | null;
  bsp?: number | null;
  sog?: number | null;
  heel?: number | null;
  twa?: number | null;
  tws?: number | null;
  rudder?: number | null;
  twa_abs?: number | null;
  rudder_abs?: number | null;
  rot?: number | null;
  hdg?: number | null;
  trim?: number | null;
  globalIndex?: number;
}

/** Claves numéricas que sí existen en TrackPoint */
type NumericKey = keyof Pick<
  TrackPoint,
  | 'vmg'
  | 'bsppc'
  | 'vmgpc'
  | 'bsp'
  | 'sog'
  | 'heel'
  | 'twa'
  | 'tws'
  | 'rudder'
  | 'twa_abs'
  | 'rudder_abs'
  | 'rot'
  | 'hdg'
  | 'trim'
>;

/** Bounds externos que llegan desde SidePanelPage (forma larga) */
type ExternalMetricBounds = {
  max: Partial<Record<NumericKey | 'tws' | 'twa_abs', number>>;
  min: Partial<Record<NumericKey | 'tws' | 'twa_abs', number>>;
  iqrMin?: Partial<Record<NumericKey | 'tws' | 'twa_abs', number>>;
  iqrMax?: Partial<Record<NumericKey | 'tws' | 'twa_abs', number>>;
};

type FilterReasons = {
  twsNotFinite: number;
  twsOutOfRange: number;
  twaNotFinite: number;
  twaAngleInvalid: number;
  valueNotFinite: number;
};

type FilterStats = {
  total: number;
  kept: number;
  dropped: number;
  reasons: FilterReasons;
  variable: NumericKey;
  twsRange: { min: number; max: number };
  sampleKept: TrackPoint[];
  sampleDroppedHints?: { tws?: number | null; twa?: number | null; val?: number | null }[];
};

export interface PolarPanelProperties {
  trackData: TrackPoint[];
  metricBounds?: ExternalMetricBounds;
  polars?: {
    angles: number[];
    tws: number[];
    speeds: number[][];
    stds: number[][];
    raw: string | null;
  };
}

/* =========================== Utilidades =========================== */
function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}
function coerceFiniteNumber(value: unknown): number | null {
  if (isFiniteNumber(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return null;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}
function normalizeAngle180(angle: number): number {
  const a = (((angle + 180) % 360) + 360) % 360;
  return a - 180;
}
function twaTo0_180(twa: number): number {
  const norm = normalizeAngle180(twa);
  return Math.abs(norm);
}

function getVariableValue(r: TrackPoint, key: NumericKey): number | null {
  // Use absolute TWA when visualizing TWA so 0–180° binning does not lose information
  if (key === 'twa') {
    const v = coerceFiniteNumber(r.twa);
    if (v === null) {
      return null;
    }
    return twaTo0_180(v);
  }
  const v = coerceFiniteNumber(r[key]);
  if (v === null) {
    return null;
  }
  return v;
}
function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}
function approxInt(n: number) {
  return Math.round(n);
}

const POLAR_COLOR_SCALE = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
];

function pickColorByTws(tws: number, min: number, max: number) {
  if (!Number.isFinite(tws)) {
    return POLAR_COLOR_SCALE[0];
  }

  const t = clamp((tws - min) / Math.max(1e-6, max - min), 0, 1);
  const index = Math.min(
    POLAR_COLOR_SCALE.length - 1,
    Math.round(t * (POLAR_COLOR_SCALE.length - 1)),
  );
  return POLAR_COLOR_SCALE[index];
}

/** Medición de texto con canvas para reservar márgenes de ticks */
const textMeasurer = (() => {
  let canvas: HTMLCanvasElement | null = null;
  let context: CanvasRenderingContext2D | null = null;
  const cache = new Map<string, { w: number; h: number }>();
  return function measure(text: string, font = '12px sans-serif') {
    const key = `${font}|${text}`;
    const hit = cache.get(key);
    if (hit) {
      return hit;
    }
    if (!canvas) {
      canvas = document.createElement('canvas');
      context = canvas.getContext('2d');
    }
    if (!context) {
      return { w: text.length * 6, h: 12 };
    }
    context.font = font;
    const metrics = context.measureText(text);
    const h = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent || 12;
    const w = metrics.width || text.length * 6;
    const value = { w: Math.ceil(w), h: Math.ceil(h) };
    cache.set(key, value);
    return value;
  };
})();

/** Mide el <g.angleAxis> con getBBox(). Devuelve {w,h} redondeados o {w:0,h:0} si falla. */
function measureAngleAxisSize(root: HTMLElement | null): {
  w: number;
  h: number;
} {
  if (!root) {
    return { w: 0, h: 0 };
  }
  try {
    const element = root.querySelector('g.recharts-layer.recharts-polar-angle-axis.angleAxis');
    // getBBox sólo existe en elementos SVG
    if (element instanceof SVGGraphicsElement) {
      const bbox = element.getBBox();
      // Evitar NaN si el nodo está oculto
      const w = Number.isFinite(bbox.width) ? Math.round(bbox.width) : 0;
      const h = Number.isFinite(bbox.height) ? Math.round(bbox.height) : 0;
      return { w, h };
    }
    return { w: 0, h: 0 };
  } catch {
    return { w: 0, h: 0 };
  }
}

function niceFloor(x: number) {
  if (!Number.isFinite(x)) {
    return 0;
  }
  const ax = Math.abs(x);
  const m = Math.pow(10, Math.floor(Math.log10(Math.max(1e-9, ax))));
  const step = m / 2; // 0.5 * 10^k
  return Math.sign(x) * Math.floor(ax / step) * step;
}
function niceCeil(x: number) {
  if (!Number.isFinite(x)) {
    return 0;
  }
  const ax = Math.abs(x);
  const m = Math.pow(10, Math.floor(Math.log10(Math.max(1e-9, ax))));
  const step = m / 2; // 0.5 * 10^k
  return Math.sign(x) * Math.ceil(ax / step) * step;
}

function quantileSorted(array: number[], p: number) {
  if (!Array.isArray(array) || array.length === 0) {
    return Number.NaN;
  }
  const index = (array.length - 1) * clamp(p, 0, 1);
  const lo = Math.floor(index);
  const hi = Math.ceil(index);
  const h = index - lo;
  if (lo === hi) {
    return array[lo];
  }
  return array[lo] * (1 - h) + array[hi] * h;
}
function robustExtent(values: number[], lowerP = 0.02, upperP = 0.98) {
  if (!Array.isArray(values) || values.length === 0) {
    return { lo: Number.NaN, hi: Number.NaN };
  }
  const a = [...values].filter((v) => Number.isFinite(v)).sort((x, y) => x - y);
  if (a.length === 0) {
    return { lo: Number.NaN, hi: Number.NaN };
  }
  const lo = quantileSorted(a, lowerP);
  const hi = quantileSorted(a, upperP);
  return { lo, hi };
}

/* =========================== Componente =========================== */
// Tipos mínimos para shape personalizado del Radar
type RadarPointPayload = {
  angle?: number;
  angleLabel?: string;
  [key: string]: unknown;
};
type RadarPoint = { x: number; y: number; payload?: RadarPointPayload };
type RadarCustomShapeProperties = {
  points?: RadarPoint[];
  stroke?: string;
  strokeWidth?: number;
  strokeOpacity?: number;
};

// Shape that DOES NOT connect gaps: splits into segments when angle jumps
// Shape que SIEMPRE conecta puntos válidos en orden.
// Si un tramo queda con 1 solo punto, dibuja además un pequeño dot para que sea visible.
function OpenRadarShape(properties: RadarCustomShapeProperties) {
  const { points, stroke, strokeWidth, strokeOpacity } = properties;
  if (!Array.isArray(points) || points.length === 0) return null;

  // puntos válidos en el orden recibido
  const valid: RadarPoint[] = points.filter((p): p is RadarPoint =>
    Boolean(p && Number.isFinite(p.x) && Number.isFinite(p.y)),
  );
  if (valid.length === 0) return null;

  // Construir un único path conectando todos los puntos válidos en orden
  const cmds: string[] = [];
  for (const [i, p] of valid.entries()) {
    if (i === 0) cmds.push(`M ${p.x},${p.y}`);
    else cmds.push(`L ${p.x},${p.y}`);
  }

  // Además, si sólo hay 1 punto visible, dibuja un pequeño marcador
  const single =
    valid.length === 1 ? (
      <circle
        cx={valid[0].x}
        cy={valid[0].y}
        r={Math.max(1, strokeWidth ?? 2)}
        fill={stroke ?? TEXT_MUTED_COLOR}
        opacity={strokeOpacity}
      />
    ) : null;

  return (
    <g>
      <path
        d={cmds.join(' ')}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
        opacity={strokeOpacity}
      />
      {single}
    </g>
  );
}

// Tooltip personalizado para mostrar valores por ángulo
type TooltipPayloadItem = {
  value?: number;
  name?: string;
  color?: string;
  stroke?: string;
  dataKey?: string | number;
};
type PolarTooltipProperties = {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string | number;
};

function PolarTooltipContent(properties: PolarTooltipProperties) {
  const { t } = useTranslation('common');
  const { active, payload, label } = properties;
  if (!active || !payload || !Array.isArray(payload) || payload.length === 0) {
    return null;
  }
  // payload trae una entrada por <Radar dataKey=...>
  // Filtrar solo valores numéricos y mostrar nombre y valor redondeado
  const rows = payload
    .filter((p) => Number.isFinite(Number(p?.value)))
    .map((p, index: number) => {
      const name = typeof p.name === 'string' ? p.name : String(p.dataKey ?? `S${index + 1}`);
      const col = p.color || p.stroke || TEXT_MUTED_COLOR;
      const v = Number(p.value);
      return (
        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              width: 10,
              height: 10,
              background: col,
              display: 'inline-block',
              borderRadius: 2,
              border: `1px solid ${BORDER_SUBTLE_COLOR}`,
            }}
          />
          <span style={{ flex: 1, fontSize: 12 }}>{name}</span>
          <strong style={{ fontSize: 12 }}>{v.toFixed(2)}</strong>
        </div>
      );
    });
  // Encabezado con ángulo
  const angleTxt = typeof label === 'number' || typeof label === 'string' ? `${label}°` : '';
  return (
    <div
      style={{
        background: 'var(--surface)',
        border: `1px solid ${BORDER_SUBTLE_COLOR}`,
        borderRadius: 8,
        padding: '8px 10px',
        boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
        color: 'var(--contrast)',
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, color: 'var(--contrast)' }}>
        {t('polar.angle', 'Angle')} {angleTxt}
      </div>
      <div style={{ display: 'grid', gap: 4 }}>
        {rows.length > 0 ? (
          rows
        ) : (
      <div style={{ fontSize: 12, color: TEXT_MUTED_COLOR }}>{t('polar.noData', 'No data')}</div>
        )}
      </div>
    </div>
  );
}

// Claves posibles (constante estable) – sacada del componente para no afectar deps
const POSSIBLE_NUMERIC_KEYS: NumericKey[] = [
  'vmg',
  'bsppc',
  'vmgpc',
  'bsp',
  'sog',
  'heel',
  'twa',
  'tws',
  'rudder',
  'twa_abs',
  'rudder_abs',
  'rot',
  'hdg',
  'trim',
];

export function PolarPanel({ trackData, metricBounds, polars }: PolarPanelProperties) {
  const { t } = useTranslation('common');
  const theme = useMantineTheme();
  useComputedColorScheme();

  const hasPolars =
    !!polars &&
    Array.isArray(polars.angles) &&
    Array.isArray(polars.tws) &&
    Array.isArray(polars.speeds) &&
    Array.isArray(polars.stds) &&
    polars.angles.length > 0 &&
    polars.tws.length > 0 &&
    polars.speeds.length === polars.angles.length &&
    polars.stds.length === polars.angles.length;

  const numericKeys = useMemo(() => {
    const present = new Set<NumericKey>();
    for (const k of POSSIBLE_NUMERIC_KEYS) {
      for (const r of trackData) {
        const v = r[k];
        if (isFiniteNumber(v as number)) {
          present.add(k);
          break;
        }
      }
    }
    return present;
  }, [trackData]);

  const [variableKey, setVariableKey] = useState<NumericKey>('sog');

  useEffect(() => {
    if (numericKeys.size === 0) {
      return;
    }
    if (!numericKeys.has(variableKey)) {
      const pref: NumericKey | undefined = (
        numericKeys.has('sog') ? 'sog' : [...numericKeys][0]
      ) as NumericKey | undefined;
      if (pref) {
        setVariableKey(pref);
      }
    }
  }, [numericKeys, variableKey]);

  const [twsMinMax, setTwsMinMax] = useState<{ min: number; max: number }>({
    min: metricBounds?.min?.tws ?? 0,
    max: metricBounds?.max?.tws ?? 30,
  });

  // TWS por defecto: prioriza datos reales; si no hay, cae a polares
  const computeDefaultTws = useCallback((): string | null => {
    const counts = new Map<number, number>();
    for (const r of trackData) {
      const v = Number(r.tws);
      if (!Number.isFinite(v)) continue;
      const k = Math.round(v);
      counts.set(k, (counts.get(k) || 0) + 1);
    }

    if (counts.size === 0) {
      if (hasPolars && polars && Array.isArray(polars.tws) && polars.tws.length > 0) {
        const candidate = polars.tws
          .map((n) => Math.round(Number(n)))
          .filter((n) => Number.isFinite(n))
          .sort((a, b) => a - b)[0];
        return Number.isFinite(candidate as number) ? String(candidate) : null;
      }
      return null;
    }

    let candidates: number[];
    if (hasPolars && polars && Array.isArray(polars.tws) && polars.tws.length > 0) {
      candidates = polars.tws.map(Number).filter((n) => Number.isFinite(n));
      if (candidates.length === 0) return null;
    } else {
      candidates = [...counts.keys()];
    }

    let best: number | null = null;
    let bestCount = -1;
    for (const c of candidates) {
      const cnt = counts.get(Math.round(c)) || 0;
      if (cnt > bestCount) {
        best = Math.round(c);
        bestCount = cnt;
      }
    }
    return best !== null && bestCount > 0 ? String(best) : null;
  }, [trackData, hasPolars, polars]);

  const [selectedTwsLines, setSelectedTwsLines] = useState<string[]>(() => {
    const d = computeDefaultTws();
    return d ? [d] : [];
  });

  // Si los polares llegan tarde y no hay selección, elegir el primer TWS disponible
  useEffect(() => {
    if (selectedTwsLines.length > 0) {
      return;
    }
    if (hasPolars && polars && Array.isArray(polars.tws) && polars.tws.length > 0) {
      const candidate = polars.tws
        .map((n) => Math.round(Number(n)))
        .filter((n) => Number.isFinite(n))
        .sort((a, b) => a - b)[0];
      if (Number.isFinite(candidate as number)) {
        setSelectedTwsLines([String(candidate)]);
      }
    }
  }, [hasPolars, polars, selectedTwsLines.length]);
  // Si no hay polares y llegan datos reales, elegir TWS por defecto desde trackData
  useEffect(() => {
    if (selectedTwsLines.length > 0) {
      return;
    }
    if (!hasPolars && trackData && trackData.length > 0) {
      const d = computeDefaultTws();
      if (d) {
        setSelectedTwsLines([d]);
      }
    }
  }, [trackData, hasPolars, selectedTwsLines.length, computeDefaultTws]);
  const [twaGranularity, setTwaGranularity] = useState<string>('2');

  const [showDeviations, setShowDeviations] = useState<boolean>(false);
  const [showPolarLines, setShowPolarLines] = useState<boolean>(false);
  const [showData, setShowData] = useState<boolean>(true);

  const [processing, setProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [hoveredTws, setHoveredTws] = useState<string | null>(null);
  const [pressedTws, setPressedTws] = useState<string | null>(null);

  const chartContainerReference = useRef<HTMLDivElement | null>(null);

  // Horizontal legend scroll handling
  const twsScrollReference = useRef<HTMLDivElement | null>(null);
  const previousScrollWidthReference = useRef<number>(0);

  // Stable key representing selected TWS list for hook dependencies
  const selectedTwsKey = useMemo(() => selectedTwsLines.join(','), [selectedTwsLines]);

  const onLegendWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    const element = twsScrollReference.current;
    if (!element) {
      return;
    }
    // Map vertical wheel to horizontal scroll for easier navigation
    if (Math.abs(event.deltaX) < Math.abs(event.deltaY)) {
      element.scrollLeft += event.deltaY;
      event.preventDefault();
    }
  }, []);

  // Auto-scroll when content grows; preserve position otherwise
  useLayoutEffect(() => {
    const element = twsScrollReference.current;
    if (!element) {
      return;
    }
    const w = element.scrollWidth;
    // If content width increased, scroll to the end to reveal new chips
    if (w > previousScrollWidthReference.current + 4) {
      element.scrollTo({ left: element.scrollWidth, behavior: 'smooth' });
    }
    previousScrollWidthReference.current = w;
  }, [selectedTwsKey]);

  /* ===== Tamaño del radar y márgenes seguros para ticks ===== */
  const [outerRadius, setOuterRadius] = useState<number>(120);
  const [gridRadii, setGridRadii] = useState<number[]>([
    0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150, 160, 170, 180,
  ]);
  const [rotate90, setRotate90] = useState<boolean>(false);
  const [chartMargins, setChartMargins] = useState<{
    top: number;
    right: number;
    bottom: number;
    left: number;
  }>({ top: 12, right: 12, bottom: 12, left: 12 });

  const [containerSize, setContainerSize] = useState<{ w: number; h: number }>({
    w: 0,
    h: 0,
  });
  const [_innerSize, setInnerSize] = useState<{ w: number; h: number }>({
    w: 0,
    h: 0,
  });

  /** Tamaño medido del <g.angleAxis> (svgRadar) */
  const [svgRadarSize, setSvgRadarSize] = useState<{ w: number; h: number }>({
    w: 0,
    h: 0,
  });

  // Dynamic angle tick density and font size based on outerRadius
  const angleTickCount = useMemo(() => {
    if (outerRadius < 70) {
      return 10;
    } // ~every 18°
    if (outerRadius < 100) {
      return 14;
    } // ~every 13°
    return 20; // ~every 9°
  }, [outerRadius]);

  const angleTickFontPx = useMemo(() => {
    if (outerRadius < 70) {
      return 9;
    }
    if (outerRadius < 100) {
      return 10;
    }
    return 12;
  }, [outerRadius]);

  /** Bounds inferidos para variable y TWS */
  const inferredBounds = useMemo(() => {
    let twsMin = Number.POSITIVE_INFINITY;
    let twsMax = Number.NEGATIVE_INFINITY;
    let variableMin = Number.POSITIVE_INFINITY;
    let variableMax = Number.NEGATIVE_INFINITY;

    for (const r of trackData) {
      const tws = Number(r.tws);
      const vk = getVariableValue(r, variableKey);
      if (isFiniteNumber(tws)) {
        if (tws < twsMin) {
          twsMin = tws;
        }
        if (tws > twsMax) {
          twsMax = tws;
        }
      }
      if (isFiniteNumber(vk as number)) {
        const vnum = vk as number;
        if (vnum < variableMin) {
          variableMin = vnum;
        }
        if (vnum > variableMax) {
          variableMax = vnum;
        }
      }
    }

    if (!Number.isFinite(twsMin)) {
      twsMin = 0;
    }
    if (!Number.isFinite(twsMax)) {
      twsMax = 30;
    }
    if (!Number.isFinite(variableMin)) {
      variableMin = 0;
    }
    if (!Number.isFinite(variableMax)) {
      variableMax = Math.max(10, variableMin + 10);
    }

    const extensionMinTws = metricBounds?.min?.tws;
    const extensionMaxTws = metricBounds?.max?.tws;
    const extensionMinVariable = metricBounds?.min?.[variableKey];
    const extensionMaxVariable = metricBounds?.max?.[variableKey];

    const finalTwsMin = isFiniteNumber(extensionMinTws) ? extensionMinTws : twsMin;
    const finalTwsMax = isFiniteNumber(extensionMaxTws) ? extensionMaxTws : twsMax;
    const finalVariableMin = isFiniteNumber(extensionMinVariable)
      ? extensionMinVariable
      : variableMin;
    const finalVariableMax = isFiniteNumber(extensionMaxVariable)
      ? extensionMaxVariable
      : variableMax;

    return {
      tws: { min: finalTwsMin, max: finalTwsMax },
      variable: { min: finalVariableMin, max: finalVariableMax },
    };
  }, [trackData, variableKey, metricBounds]);

  /* =========================== Utilidades de layout =========================== */
  function getMaxSemicircleRadius(innerW: number, innerH: number, vertical: boolean) {
    if (vertical) {
      const rByW = innerW - 18;
      const rByH = innerH / 2;
      return Math.floor(Math.max(0, Math.min(rByW, rByH)));
    }
    const rByW = innerW / 2;
    const rByH = innerH - 18;
    return Math.floor(Math.max(0, Math.min(rByW, rByH)));
  }

  useEffect(() => {
    setTwsMinMax({
      min: inferredBounds.tws.min,
      max: inferredBounds.tws.max,
    });
  }, [inferredBounds.tws.min, inferredBounds.tws.max]);

  useLayoutEffect(() => {
    const element = chartContainerReference.current;
    if (!element) {
      return;
    }

    function compute(w: number, h: number, origin: string) {
      const width = w > 1 ? w : MIN_CHART_WIDTH;
      const height = h > 1 ? h : MIN_CHART_HEIGHT;
      setContainerSize({ w: Math.round(width), h: Math.round(height) });

      const aspect = width / Math.max(1, height);
      const vertical = aspect < 1;
      setRotate90(vertical);

      const fontPx = 12;
      const font = `${fontPx}px ${theme.fontFamily || 'sans-serif'}`;

      // Etiquetas de ángulo
      const angleLabels = [
        '0°',
        '10°',
        '20°',
        '30°',
        '40°',
        '50°',
        '60°',
        '70°',
        '80°',
        '90°',
        '100°',
        '110°',
        '120°',
        '130°',
        '140°',
        '150°',
        '160°',
        '170°',
        '180°',
      ];
      let angleMaxW = 0;
      let angleMaxH = 0;
      for (const t of angleLabels) {
        const { w: tw, h: th } = textMeasurer(t, font);
        angleMaxW = Math.max(angleMaxW, tw);
        angleMaxH = Math.max(angleMaxH, th);
      }

      // Texto de radio
      const maxRad = Math.abs(inferredBounds.variable.max || 0);
      const radLabel = String(Math.round(maxRad));
      const { w: radW, h: radH } = textMeasurer(radLabel || '0', font);

      const padGap = 6;

      // Márgenes
      let left = Math.ceil(angleMaxW / 2) + padGap;
      let right = Math.ceil(Math.max(angleMaxW / 2, radW) + padGap);
      let top = angleMaxH + padGap;
      let bottom = Math.ceil(angleMaxH / 2) + padGap;

      if (vertical) {
        bottom = Math.ceil(Math.max(angleMaxH, radH) + padGap);
        top = Math.ceil(angleMaxH / 2) + padGap;
        left = Math.ceil(angleMaxH / 2) + padGap;
        right = Math.ceil(Math.max(angleMaxH / 2, radW) + padGap);
      }

      const innerW = Math.max(0, width - left - right);
      const innerH = Math.max(0, height - top - bottom);
      setInnerSize({ w: Math.round(innerW), h: Math.round(innerH) });

      // Radio base
      const baseR = getMaxSemicircleRadius(innerW, innerH, vertical);

      // Despeje
      const labelClearX = Math.ceil(Math.max(angleMaxW / 2, radW / 2));
      const labelClearY = Math.ceil(Math.max(angleMaxH, radH) / 2) + 2;

      let R = Math.max(0, baseR - (vertical ? labelClearY : labelClearX));

      const MIN_R = 40;
      if (R < MIN_R) {
        const shrink = MIN_R - R;
        const factor = 0.5;
        left = Math.max(0, left - shrink * factor);
        right = Math.max(0, right - shrink * factor);
        top = Math.max(0, top - shrink * factor);
        bottom = Math.max(0, bottom - shrink * factor);

        const innerW2 = Math.max(0, width - left - right);
        const innerH2 = Math.max(0, height - top - bottom);
        setInnerSize({ w: Math.round(innerW2), h: Math.round(innerH2) });

        const baseR2 = getMaxSemicircleRadius(innerW2, innerH2, vertical);
        R = Math.max(0, baseR2 - (vertical ? labelClearY : labelClearX));
      }

      R = Math.max(0, R);
      setOuterRadius(R);
      setGridRadii([0, R * 0.25, R * 0.5, R * 0.75, R]);
      setChartMargins({
        top: Math.round(top),
        right: Math.round(right),
        bottom: Math.round(bottom),
        left: Math.round(left),
      });
    }

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cr = entry.contentRect;
        compute(cr.width, cr.height, 'observer');
      }
    });
    ro.observe(element);
    const rect = element.getBoundingClientRect();
    compute(rect.width, rect.height, 'initial');
    return () => ro.disconnect();
  }, [theme.fontFamily, inferredBounds.variable.max]);

  /* ---------- Opciones de TWS ---------- */
  const twsLineOptions = useMemo(() => {
    if (hasPolars) {
      return polars.tws
        .map(Number)
        .filter((n) => Number.isFinite(n))
        .sort((a, b) => a - b)
        .map((n) => ({ value: String(n), label: `${n} kts` }));
    }
    const ints = new Set<number>();
    for (const r of trackData) {
      const v = r.tws;
      if (isFiniteNumber(v)) {
        const index = approxInt(v);
        if (
          index >= Math.floor(inferredBounds.tws.min) &&
          index <= Math.ceil(inferredBounds.tws.max)
        ) {
          ints.add(index);
        }
      }
    }
    const array = [...ints].sort((a, b) => a - b);
    return array.map((n) => ({ value: String(n), label: `${n} kts` }));
  }, [hasPolars, polars, trackData, inferredBounds.tws.min, inferredBounds.tws.max]);

  /* ---------- Filtrado ---------- */
  // Detailed filtering diagnostics
  const filterStats = useMemo<FilterStats>(() => {
    const stats: FilterStats = {
      total: trackData.length,
      kept: 0,
      dropped: 0,
      reasons: {
        twsNotFinite: 0,
        twsOutOfRange: 0,
        twaNotFinite: 0,
        twaAngleInvalid: 0,
        valueNotFinite: 0,
      },
      variable: variableKey,
      twsRange: { min: twsMinMax.min, max: twsMinMax.max },
      sampleKept: [],
      sampleDroppedHints: [],
    };

    for (const r of trackData) {
      const tws = Number(r.tws);
      if (!Number.isFinite(tws)) {
        stats.reasons.twsNotFinite++;
        stats.dropped += 1;
        if ((stats.sampleDroppedHints?.length ?? 0) < 5) {
          stats.sampleDroppedHints?.push({ tws: r.tws ?? null, twa: r.twa ?? null });
        }
        continue;
      }
      if (tws < twsMinMax.min || tws > twsMinMax.max) {
        stats.reasons.twsOutOfRange++;
        stats.dropped += 1;
        if ((stats.sampleDroppedHints?.length ?? 0) < 5) {
          stats.sampleDroppedHints?.push({ tws, twa: r.twa ?? null });
        }
        continue;
      }
      const twa = Number(r.twa);
      if (!Number.isFinite(twa)) {
        stats.reasons.twaNotFinite++;
        stats.dropped += 1;
        if ((stats.sampleDroppedHints?.length ?? 0) < 5) {
          stats.sampleDroppedHints?.push({ tws, twa: r.twa ?? null });
        }
        continue;
      }
      const ang = twaTo0_180(twa);
      if (!Number.isFinite(ang) || ang < 0 || ang > 180) {
        stats.reasons.twaAngleInvalid++;
        stats.dropped += 1;
        if ((stats.sampleDroppedHints?.length ?? 0) < 5) {
          stats.sampleDroppedHints?.push({ tws, twa });
        }
        continue;
      }
      const value = getVariableValue(r, variableKey);
      if (!isFiniteNumber(value as number)) {
        stats.reasons.valueNotFinite++;
        stats.dropped += 1;
        if ((stats.sampleDroppedHints?.length ?? 0) < 5) {
          stats.sampleDroppedHints?.push({ tws, twa, val: value ?? null });
        }
        continue;
      }
      stats.kept += 1;
      if (stats.sampleKept.length < 5) {
        stats.sampleKept.push(r);
      }
    }

    return {
      ...stats,
      sampleDroppedHints:
        stats.sampleDroppedHints && stats.sampleDroppedHints.length > 0
          ? stats.sampleDroppedHints
          : undefined,
    };
  }, [trackData, variableKey, twsMinMax.min, twsMinMax.max]);

  const filtered = useMemo(() => {
    const out: TrackPoint[] = [];
    for (const r of trackData) {
      // Do not filter out by UTC parseability. Some backends provide
      // non-ISO date strings (e.g., without 'T' or timezone) or seconds-based
      // timestamps, and we do not need a valid Date object for polar plots.

      const tws = Number(r.tws);
      if (!isFiniteNumber(tws)) {
        continue;
      }
      if (tws < twsMinMax.min || tws > twsMinMax.max) {
        continue;
      }

      const twa = Number(r.twa);
      if (!isFiniteNumber(twa)) {
        continue;
      }
      const ang = twaTo0_180(twa);
      if (!isFiniteNumber(ang) || ang < 0 || ang > 180) {
        continue;
      }

      const value = getVariableValue(r, variableKey);
      if (!isFiniteNumber(value as number)) {
        continue;
      }

      out.push(r);
    }
    return out;
  }, [trackData, twsMinMax.min, twsMinMax.max, variableKey]);

  // Emit filtering diagnostics to console
  useEffect(() => {
    try {
      const keptSample = preview(filtered, 5);
      dbg('filter.stats', {
        total: filterStats.total,
        kept: filterStats.kept,
        dropped: filterStats.dropped,
        reasons: filterStats.reasons,
        variable: filterStats.variable,
        twsRange: filterStats.twsRange,
        keptSample,
      });
    } catch {}
  }, [filtered, filterStats]);

  /* ---------- Series agregadas ---------- */
  type SeriesPoint = {
    angle: number;
    angleLabel: string;
    value: number | null; // mean
    upper: number | null; // mean + stdev
    lower: number | null; // mean - stdev
    count: number;
    twsMean: number;
  };
  type SeriesMap = Record<string, SeriesPoint[]>;

  const interpolateAggregatedSeries = useCallback(
    (input: SeriesPoint[], gapDegrees = 20): SeriesPoint[] => {
      if (!Array.isArray(input) || input.length === 0) {
        return input;
      }
      const defined = input.filter((p) => p.count > 0 && Number.isFinite(p.value as number));
      if (defined.length < 2) {
        return input;
      }
      const sorted = [...defined].sort((a, b) => a.angle - b.angle);
      return input.map((point) => {
        if (Number.isFinite(point.value as number)) {
          return point;
        }
        const angle = point.angle;
        let left = null as SeriesPoint | null;
        let right = null as SeriesPoint | null;
        for (const candidate of sorted) {
          if (candidate.angle < angle) {
            if (!left || candidate.angle > left.angle) {
              left = candidate;
            }
          } else if (candidate.angle > angle) {
            right = candidate;
            break;
          }
        }
        if (!left || !right) {
          return point;
        }
        const gap = right.angle - left.angle;
        if (!(gap > 0) || gap > gapDegrees) {
          return point;
        }
        const t = gap === 0 ? 0 : (angle - left.angle) / gap;
        const leftValue = left.value as number;
        const rightValue = right.value as number;
        const value = leftValue + (rightValue - leftValue) * t;
        const leftTws = Number.isFinite(left.twsMean) ? left.twsMean : Number.NaN;
        const rightTws = Number.isFinite(right.twsMean) ? right.twsMean : Number.NaN;
        const twsMean =
          Number.isFinite(leftTws) && Number.isFinite(rightTws)
            ? leftTws + (rightTws - leftTws) * t
            : point.twsMean;
        return {
          ...point,
          value,
          twsMean: Number.isFinite(twsMean) ? twsMean : point.twsMean,
        };
      });
    },
    [],
  );

  const [bin, series] = useMemo(() => {
    setProcessing(true);
    setError(null);
    try {
      const selected = selectedTwsLines.length > 0 ? selectedTwsLines.map(Number) : [];
      const binLocal = Math.max(1, Math.min(30, Number(twaGranularity || '5')));

      if (selected.length === 0) {
        return [binLocal, {} as SeriesMap] as const;
      }

      // Branch 2: aggregate from raw trackData
      const byKey: Record<
        string,
        {
          [binAngle: number]: {
            sum: number;
            sumSq: number;
            n: number;
            twsSum: number;
          };
        }
      > = {};
      for (const target of selected) {
        byKey[`${target}`] = {};
      }

      // Tolerancia para considerar que un punto pertenece a la línea TWS seleccionada
      const twsTolerance = 1; // antes: 0.5

      for (const r of filtered) {
        const tws = Number(r.tws);
        const value = getVariableValue(r, variableKey);
        const twa = Number(r.twa);
        if (!isFiniteNumber(tws) || !isFiniteNumber(value as number) || !isFiniteNumber(twa)) {
          continue;
        }
        const angle = twaTo0_180(twa);

        for (const target of selected) {
          if (Math.abs(tws - target) <= twsTolerance) {
            const angleBin = Math.round(angle / binLocal) * binLocal;
            const k = `${target}`;
            const bucket = byKey[k][angleBin] ?? {
              sum: 0,
              sumSq: 0,
              n: 0,
              twsSum: 0,
            };
            const vnum = value as number;
            bucket.sum += vnum;
            bucket.sumSq += vnum * vnum;
            bucket.twsSum += tws;
            bucket.n += 1;
            byKey[k][angleBin] = bucket;
          }
        }
      }

      const out: SeriesMap = {};
      for (const [k, buckets] of Object.entries(byKey)) {
        const points: SeriesPoint[] = Object.entries(buckets)
          .map(([angleString, agg]) => {
            const ang = clamp(Number(angleString), 0, 180);
            const n = Math.max(1, agg.n);
            const mean = agg.sum / n;
            const variance = Math.max(0, agg.sumSq / n - mean * mean);
            const stdev = Math.sqrt(variance);
            return {
              angle: ang,
              angleLabel: String(ang),
              value: mean,
              upper: mean + stdev,
              lower: mean - stdev,
              twsMean: agg.twsSum / n,
              count: agg.n,
            };
          })
          .sort((a, b) => a.angle - b.angle);

        const filled: SeriesPoint[] = [];
        for (let a = 0; a <= 180; a += binLocal) {
          const p = points.find((x) => x.angle === a);
          if (p) {
            filled.push(p);
          } else {
            filled.push({
              angle: a,
              angleLabel: String(a),
              value: null,
              upper: null,
              lower: null,
              twsMean: Number.NaN,
              count: 0,
            });
          }
        }

        const smoothed = interpolateAggregatedSeries(filled, Math.max(20, binLocal * 3));

        if (smoothed.length > 0) {
          smoothed[0] = {
            ...smoothed[0],
            value: null,
            upper: null,
            lower: null,
            count: 0,
            twsMean: Number.NaN,
          };
          const lastItem = smoothed.at(-1);
          if (lastItem) {
            smoothed[smoothed.length - 1] = {
              ...lastItem,
              value: null,
              upper: null,
              lower: null,
              count: 0,
              twsMean: Number.NaN,
            };
          }
        }
        out[k] = smoothed;
      }

      return [binLocal, out] as const;
    } catch (error_: unknown) {
      setError(
        error_ instanceof Error
          ? error_.message
          : 'Data processing failed. Please check filters and variable selection.',
      );
      return [Math.max(1, Math.min(30, Number(twaGranularity || '5'))), {} as SeriesMap] as const;
    } finally {
      setProcessing(false);
    }
  }, [filtered, variableKey, selectedTwsLines, twaGranularity, interpolateAggregatedSeries]);

  // Emit aggregation diagnostics
  useEffect(() => {
    try {
      const perTws = Object.fromEntries(
        Object.entries(series).map(([k, pts]) => [
          k,
          {
            points: pts.length,
            nonNull: pts.filter((p) => Number.isFinite(p.value as number)).length,
            withCount: pts.filter((p) => (p.count || 0) > 0).length,
            sample: preview(pts.filter((p) => (p.count || 0) > 0).slice(0, 5)),
          },
        ]),
      );
      dbg('series.summary', { bin, selectedTws: selectedTwsLines, perTws });
    } catch {}
  }, [series, bin, selectedTwsLines]);

  // Asegura que haya una línea TWS con datos: si la selección actual
  // no produce puntos (p.count > 0), reelige usando computeDefaultTws().
  useEffect(() => {
    // 1) Si no hay ninguna selección aún, intenta elegir una por defecto
    if (selectedTwsLines.length === 0) {
      const d = computeDefaultTws();
      if (d) {
        setSelectedTwsLines([d]);
      }
      return;
    }

    // 2) Si hay selección pero no genera datos visibles, reelige automáticamente
    try {
      const selectionHasData = selectedTwsLines.some((key) =>
        (series[key] || []).some(
          (p) => Number.isFinite(p.count as number) && (p.count as number) > 0,
        ),
      );
      if (!selectionHasData) {
        const d = computeDefaultTws();
        if (d && !selectedTwsLines.includes(d)) {
          setSelectedTwsLines([d]);
        }
      }
    } catch {
      /* silent */
    }
  }, [series, selectedTwsLines, trackData, hasPolars, polars, computeDefaultTws]);

  // Si hay polares y no hay datos agregados visibles, habilitar overlay para que se vea algo por defecto
  useEffect(() => {
    try {
      if (!hasPolars || showPolarLines) {
        return;
      }
      const anyData = Object.values(series).some((pts) =>
        pts.some((p) => Number.isFinite(p.count as number) && (p.count as number) > 0),
      );
      if (!anyData && selectedTwsLines.length > 0) {
        setShowPolarLines(true);
      }
    } catch {
      /* silent */
    }
  }, [hasPolars, series, selectedTwsLines.length, showPolarLines]);

  /* ---------- Overlay de líneas polares crudas (solo SOG) ---------- */
  const overlay = useMemo(() => {
    if (!showPolarLines || !hasPolars || variableKey !== 'sog') {
      return {} as SeriesMap;
    }
    const selected = selectedTwsLines.length > 0 ? selectedTwsLines.map(Number) : [];
    if (selected.length === 0) {
      return {} as SeriesMap;
    }

    const out: SeriesMap = {};
    const colByTws: Record<number, number> = {};
    for (const [index, t] of polars.tws.entries()) {
      const n = Number(t);
      if (Number.isFinite(n)) {
        colByTws[n] = index;
      }
    }

    const bins: number[] = [];
    for (let a = 0; a <= 180; a += bin) {
      bins.push(a);
    }

    function interpolateSeries(col: number, twsTarget: number): SeriesPoint[] {
      const pol = polars as NonNullable<typeof polars>;
      // Collect finite positive raw points
      const pairs: { a: number; v: number }[] = [];
      for (let r = 0; r < pol.angles.length; r++) {
        const ang = Number(pol.angles[r]);
        const raw = pol.speeds[r]?.[col];
        const v = Number(raw);
        if (Number.isFinite(ang) && Number.isFinite(v) && v > 0) {
          pairs.push({ a: clamp(ang, 0, 180), v });
        }
      }
      pairs.sort((p, q) => p.a - q.a);
      // unique by angle keeping last
      const uniq: { a: number; v: number }[] = [];
      for (const p of pairs) {
        const last = uniq.at(-1);
        if (uniq.length === 0 || Math.abs((last?.a ?? Infinity) - p.a) > 1e-6) {
          uniq.push(p);
        } else {
          uniq[uniq.length - 1] = p;
        }
      }

      const maxGapDeg = 20; // do not bridge large gaps
      const pts: SeriesPoint[] = bins.map((A) => {
        if (uniq.length === 0) {
          return {
            angle: A,
            angleLabel: String(A),
            value: null,
            upper: null,
            lower: null,
            twsMean: twsTarget,
            count: 0,
          };
        }
        // Exact hit (within half a bin)
        for (const element of uniq) {
          if (Math.abs(element.a - A) <= bin / 2 - 1e-6) {
            const value = element.v;
            return {
              angle: A,
              angleLabel: String(A),
              value: value,
              upper: null,
              lower: null,
              twsMean: twsTarget,
              count: 1,
            };
          }
        }
        // Find neighbors
        let li = -1;
        for (const [index, element] of uniq.entries()) {
          if (element.a <= A) {
            li = index;
          } else {
            break;
          }
        }
        const ri = li >= 0 ? li + 1 : -1;
        if (li < 0 || ri < 0 || ri >= uniq.length) {
          return {
            angle: A,
            angleLabel: String(A),
            value: null,
            upper: null,
            lower: null,
            twsMean: twsTarget,
            count: 0,
          };
        }
        const a0 = uniq[li].a;
        const a1 = uniq[ri].a;
        const gap = a1 - a0;
        if (!Number.isFinite(gap) || gap <= 0 || gap > maxGapDeg) {
          return {
            angle: A,
            angleLabel: String(A),
            value: null,
            upper: null,
            lower: null,
            twsMean: twsTarget,
            count: 0,
          };
        }
        const v0 = uniq[li].v;
        const v1 = uniq[ri].v;
        const t = (A - a0) / gap;
        const v = v0 + (v1 - v0) * t;
        return {
          angle: A,
          angleLabel: String(A),
          value: v,
          upper: null,
          lower: null,
          twsMean: twsTarget,
          count: 1,
        };
      });

      // Avoid closing the semicircle
      if (pts.length > 0) {
        pts[0] = { ...pts[0], value: null, count: 0 };
        const lastItem = pts.at(-1);
        if (lastItem) {
          pts[pts.length - 1] = { ...lastItem, value: null, count: 0 };
        }
      }
      return pts;
    }

    for (const twsTarget of selected) {
      const col = colByTws[twsTarget];
      if (col === undefined) {
        continue;
      }
      out[String(twsTarget)] = interpolateSeries(col, twsTarget);
    }

    return out;
  }, [showPolarLines, hasPolars, variableKey, selectedTwsLines, polars, bin]);
  useEffect(() => {
    try {
      const info = Object.fromEntries(
        Object.entries(overlay).map(([k, pts]) => [
          k,
          {
            points: pts.length,
            nonNull: pts.filter((p) => Number.isFinite(p.value as number)).length,
            sample: preview(
              pts.filter((p) => Number.isFinite(p.value as number)),
              5,
            ),
          },
        ]),
      );
      dbg('overlay.summary', {
        enabled: showPolarLines,
        variableKey,
        hasPolars,
        selectedTws: selectedTwsLines,
        info,
      });
    } catch {}
  }, [overlay, showPolarLines, variableKey, hasPolars, selectedTwsLines]);

  /* ---------- Data combinado para RadarChart ---------- */
  const combinedData = useMemo(() => {
    type DataKey = `v_${string}` | `vu_${string}` | `vl_${string}` | `pv_${string}`;
    type CombinedRow = { angle: number; angleLabel: string } & Partial<Record<DataKey, number>>;

    // Build base rows and a fast index by angle
    const base: CombinedRow[] = [];
    const indexByAngle = new Map<number, number>();
    for (let a = 0, i = 0; a <= 180; a += bin, i++) {
      base.push({ angle: a, angleLabel: String(a) });
      indexByAngle.set(a, i);
    }

    // inject aggregated data series
    if (showData) {
      for (const [twsKey, pts] of Object.entries(series)) {
        const f: DataKey = `v_${twsKey}`;
        const fu: DataKey = `vu_${twsKey}`;
        const fl: DataKey = `vl_${twsKey}`;

        for (const p of pts) {
          const idx = indexByAngle.get(p.angle);
          if (idx === undefined) continue;
          if (isFiniteNumber(p.value as number)) {
            base[idx][f] = p.value as number;
          }
          if (p.count > 0 && isFiniteNumber(p.upper as number)) {
            base[idx][fu] = p.upper as number;
          }
          if (p.count > 0 && isFiniteNumber(p.lower as number)) {
            base[idx][fl] = p.lower as number;
          }
        }
      }
    }

    // inject raw polar overlay
    for (const [twsKey, pts] of Object.entries(overlay)) {
      const f: DataKey = `pv_${twsKey}`;
      for (const p of pts) {
        const idx = indexByAngle.get(p.angle);
        if (idx === undefined) continue;
        if (p.count > 0 && isFiniteNumber(p.value as number)) {
          base[idx][f] = p.value as number;
        }
      }
    }

    return base;
  }, [series, overlay, bin, showData]);

  useEffect(() => {
    dbg('Combinedata', combinedData);
  }, [combinedData]);
  useEffect(() => {
    try {
      const keys = selectedTwsLines.map((s) => `v_${s}`);
      const counts = Object.fromEntries(
        keys.map((k) => {
          return [
            k,
            combinedData.filter((row) => {
              const record = row as Record<string, unknown>;
              return isFiniteNumber(record[k] as number);
            }).length,
          ];
        }),
      );
      dbg('combinedData.summary', {
        rows: combinedData.length,
        dataKeys: keys,
        definedCounts: counts,
        sampleRows: preview(combinedData, 5),
      });
    } catch {}
  }, [combinedData, selectedTwsLines]);

  const variableOptions = useMemo(
    () =>
      [...numericKeys]
        .sort()
        .map((k) => ({ value: k, label: k.toUpperCase().replaceAll('_', ' ') })),
    [numericKeys],
  );

  const twaGranularityOptions = [
    { value: '1', label: '1°' },
    { value: '2', label: '2°' },
    { value: '5', label: '5°' },
    { value: '10', label: '10°' },
    { value: '20', label: '20°' },
  ];

  // Dominio radial inteligente basado en datos visibles
  const radialDomain = useMemo<[number | 'auto', number | 'auto']>(() => {
    // Collect values from data series and from overlay separately
    const valsData: number[] = [];
    const valsOverlay: number[] = [];

    if (showData) {
      for (const pts of Object.values(series)) {
        for (const p of pts) {
          if (isFiniteNumber(p.value as number)) {
            valsData.push(p.value as number);
          }
          if (showDeviations) {
            if (isFiniteNumber(p.upper as number)) {
              valsData.push(p.upper as number);
            }
            if (isFiniteNumber(p.lower as number)) {
              valsData.push(p.lower as number);
            }
          }
        }
      }
    }

    for (const pts of Object.values(overlay)) {
      for (const p of pts) {
        if (isFiniteNumber(p.value as number)) {
          valsOverlay.push(p.value as number);
        }
      }
    }

    // Decide source for domain: if polar lines are visible and there are overlay values,
    // and data is hidden or empty, use overlay-only to avoid inflated max when opening polars.
    const useOverlayOnly =
      showPolarLines && valsOverlay.length > 0 && (!showData || valsData.length === 0);
    const source = useOverlayOnly ? valsOverlay : [...valsData, ...valsOverlay];

    const finite = source.filter((v) => Number.isFinite(v));
    if (finite.length === 0) {
      return ['auto', 'auto'];
    }

    // Robust quantiles to resist outliers
    const { lo, hi } = robustExtent(finite, 0.02, 0.98);
    const minV = Number.isFinite(lo) ? lo : Math.min(...finite);
    let maxV = Number.isFinite(hi) ? hi : Math.max(...finite);

    if (!(maxV > minV)) {
      maxV = minV + 1;
    }

    // Data-driven padding around the actually shown values
    const pad = useOverlayOnly ? 0.04 : 0.06; // fraction of range
    const range = Math.max(1e-9, maxV - minV);
    let minOut = niceFloor(minV - pad * range);
    let maxOut = niceCeil(maxV + pad * range);

    if (!Number.isFinite(minOut)) {
      minOut = Math.floor(minV);
    }
    if (!Number.isFinite(maxOut)) {
      maxOut = Math.ceil(maxV);
    }
    if (maxOut <= minOut) {
      maxOut = minOut + 1;
    }

    return [minOut, maxOut];
  }, [series, overlay, showData, showDeviations, showPolarLines]);
  useEffect(() => {
    try {
      dbg('radialDomain', {
        radialDomain,
        showData,
        showDeviations,
        showPolarLines,
      });
    } catch {}
  }, [radialDomain, showData, showDeviations, showPolarLines]);

  /* ===== Medición del <g.angleAxis> tras render ===== */
  useLayoutEffect(() => {
    let raf = 0;
    const root = chartContainerReference.current;
    if (!root) {
      return;
    }
    raf = requestAnimationFrame(() => {
      const size = measureAngleAxisSize(root);
      setSvgRadarSize(size);
    });
    return () => cancelAnimationFrame(raf);
  }, [
    containerSize.w,
    containerSize.h,
    outerRadius,
    rotate90,
    chartMargins.top,
    chartMargins.right,
    chartMargins.bottom,
    chartMargins.left,
    selectedTwsKey,
    bin,
    variableKey,
  ]);

  const cxPx = useMemo(() => {
    return rotate90
      ? Math.max(0, Math.round((containerSize.w - svgRadarSize.w) / 2)) + 16
      : containerSize.w / 2;
  }, [rotate90, containerSize.w, svgRadarSize.w]);

  const cyPx = useMemo(() => {
    return rotate90
      ? containerSize.h / 2
      : Math.min(
          containerSize.h,
          containerSize.h - Math.round((containerSize.h - svgRadarSize.h) / 2) - 16,
        );
  }, [rotate90, containerSize.h, svgRadarSize.h]);

  const safeCx = Number.isFinite(cxPx) ? cxPx : Math.max(0, containerSize.w / 2);
  const safeCy = Number.isFinite(cyPx) ? cyPx : Math.max(0, containerSize.h / 2);

  const canRenderChart =
    containerSize.w > 0 &&
    containerSize.h > 0 &&
    Number.isFinite(outerRadius) &&
    outerRadius > 0 &&
    Array.isArray(combinedData) &&
    combinedData.length > 0 &&
    Number.isFinite(safeCx) &&
    Number.isFinite(safeCy);

  useEffect(() => {
    if (canRenderChart) return;
    try {
      const reasons = {
        containerW: containerSize.w,
        containerH: containerSize.h,
        outerRadius,
        hasData: Array.isArray(combinedData) && combinedData.length > 0,
        safeCxFinite: Number.isFinite(safeCx),
        safeCyFinite: Number.isFinite(safeCy),
      };
      dbg('render.blocked', reasons);
    } catch {}
  }, [canRenderChart, containerSize.w, containerSize.h, outerRadius, combinedData, safeCx, safeCy]);

  return (
    <>
      <style>{`
  /* Ultra-hairline horizontal scrollbar for the selected TWS legend */
  .tws-scroll {
    -webkit-overflow-scrolling: touch;
    overscroll-behavior-x: contain;
    padding-bottom: 6px;
    scrollbar-gutter: stable both-edges;
    /* Firefox */
    scrollbar-width: thin;                       /* minimum available in FF */
    scrollbar-color: ${BORDER_SUBTLE_COLOR} transparent;
  }
  .tws-scroll:hover {
    scrollbar-width: thin;
    scrollbar-color: ${TEXT_MUTED_COLOR} transparent;
  }

  /* WebKit (Chromium, Safari, Edge) */
  .tws-scroll::-webkit-scrollbar {
    height: 0.5px;                 /* much thinner than before */
    width: 0;                      /* hide vertical bar */
    background: transparent;
  }
  .tws-scroll:hover::-webkit-scrollbar {
    height: 0.5px;                 /* keep hairline on hover */
  }
  .tws-scroll::-webkit-scrollbar:vertical { display: none; }
  .tws-scroll::-webkit-scrollbar-track { background: transparent; }
  .tws-scroll::-webkit-scrollbar-thumb {
    background-color: ${BORDER_SUBTLE_COLOR};
    border-radius: 9999px;
    background-clip: padding-box;
    min-width: 16px;               /* slimmer thumb */
  }
  .tws-scroll::-webkit-scrollbar-thumb:hover {
    background-color: ${TEXT_MUTED_COLOR};  /* subtle hover, no size change */
  }
`}</style>
      <Card
        className="polar-panel theme-inverted"
        withBorder
        radius="md"
        p="md"
        style={{
          position: 'relative',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: MIN_PANEL_HEIGHT,
          minWidth: MIN_PANEL_WIDTH,
        }}
      >
        <LoadingOverlay visible={processing} zIndex={1000} />

        <Stack gap="sm" style={{ flex: 0 }}>
          {error && (
            <Text c="red" size="sm">
              {error}
            </Text>
          )}

          <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 3 }} spacing="sm">
            <Select
              label={t('polar.variableLabel', 'Variable')}
              placeholder={t('polar.variablePlaceholder', 'Select variable')}
              value={variableKey}
              onChange={(v) => {
                if (!v) {
                  return;
                }
                if ((numericKeys as Set<string>).has(v)) {
                  setVariableKey(v as NumericKey);
                }
              }}
              data={variableOptions}
              searchable
              nothingFoundMessage={t('polar.nothingFound', 'No numeric variables')}
              styles={SELECT_FIELD_STYLES}
            />

            <Select
              label={t('polar.twaResolutionLabel', 'TWA Resolution')}
              placeholder={t('polar.twaResolutionPlaceholder', 'Select degrees')}
              value={twaGranularity}
              onChange={(v) => v && setTwaGranularity(v)}
              data={twaGranularityOptions}
              clearable={false}
              styles={SELECT_FIELD_STYLES}
            />

            <MultiSelect
              label={t('polar.twsLinesLabel', 'TWS lines')}
              placeholder={t('polar.twsLinesPlaceholder', 'Select TWS lines')}
              value={selectedTwsLines}
              onChange={(vals) => {
                const uniq = [...new Set(vals)];
                uniq.sort((a, b) => Number(a) - Number(b));
                setSelectedTwsLines(uniq);
              }}
              data={twsLineOptions}
              searchable
              clearable
              hidePickedOptions={false}
              checkIconPosition="left"
              withCheckIcon
              styles={{
                ...MULTI_SELECT_FIELD_STYLES,
                pill: { display: 'none' },
              }}
            />
          </SimpleGrid>

          <Group justify="flex-end" gap="md" wrap="nowrap">
            <Switch
              label={
                <Text
                  span
                  inherit
                  style={{ whiteSpace: 'nowrap', color: TEXT_CONTRAST_COLOR }}
                >
                  {t('polar.showData', 'Show Data')}
                </Text>
              }
              size="sm"
              radius="sm"
              color="var(--secondary)"
              checked={showData}
              onChange={(event) => setShowData(event.currentTarget.checked)}
              styles={{ label: { color: TEXT_CONTRAST_COLOR, fontWeight: 600 } }}
            />
            <Switch
              label={
                <Text
                  span
                  inherit
                  style={{ whiteSpace: 'nowrap', color: TEXT_CONTRAST_COLOR }}
                >
                  {t('polar.showDevs', 'Show Devs')}
                </Text>
              }
              size="sm"
              radius="sm"
              color="var(--secondary)"
              checked={showDeviations}
              onChange={(event) => setShowDeviations(event.currentTarget.checked)}
              disabled={!showData}
              styles={{ label: { color: TEXT_CONTRAST_COLOR, fontWeight: 600 } }}
              title={
                showData
                  ? ''
                  : t('polar.enableShowDataTip', "Enable 'Show data' to display deviations.")
              }
            />
            <Switch
              label={
                <Text
                  span
                  inherit
                  style={{ whiteSpace: 'nowrap', color: TEXT_CONTRAST_COLOR }}
                >
                  {t('polar.showSogTarget', 'Show SOG Tgt.')}
                </Text>
              }
              size="sm"
              radius="sm"
              color="var(--secondary)"
              checked={showPolarLines}
              onChange={(event) => {
                setShowPolarLines(event.currentTarget.checked);
              }}
              disabled={!hasPolars || variableKey !== 'sog'}
              styles={{ label: { color: TEXT_CONTRAST_COLOR, fontWeight: 600 } }}
              title={
                hasPolars
                  ? variableKey === 'sog'
                    ? ''
                    : t('polar.sogOnlyTip', 'Polar lines are only available for SOG.')
                  : t('polar.polarsRequiredTip', 'Polar lines require provided polars.')
              }
            />
          </Group>

          <Box
            className="polar-legend-container"
            role="region"
            aria-label={t('polar.twsSliderAria', 'Selected TWS horizontal slider')}
            ref={twsScrollReference}
            onWheel={onLegendWheel}
            style={{
              width: '100%',
              overflowX: 'auto',
              overflowY: 'hidden',
              whiteSpace: 'nowrap',
              scrollbarGutter: 'stable both-edges',
              WebkitOverflowScrolling: 'touch',
              overscrollBehaviorX: 'contain',
              scrollBehavior: 'smooth',
              paddingBottom: 6,
            }}
          >
            <Group
              gap="xs"
              className="tws-scroll"
              wrap="nowrap"
              style={{ flexWrap: 'nowrap', width: 'max-content' }}
            >
              {selectedTwsLines.length > 0 ? (
                selectedTwsLines.map((s) => {
                  const tws = Number(s);
                  const col = pickColorByTws(tws, inferredBounds.tws.min, inferredBounds.tws.max);
                  const handleToggle = () => {
                    setPressedTws(s);
                    setTimeout(() => {
                      const next = selectedTwsLines.filter((x) => x !== s);
                      setSelectedTwsLines(next);
                      if (hoveredTws === s) {
                        setHoveredTws(null);
                      }
                      setPressedTws(null);
                    }, 80);
                  };
                  const onKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleToggle();
                    }
                  };
                  return (
                    <Box
                      key={s}
                      role="button"
                      tabIndex={0}
                      onClick={handleToggle}
                      onKeyDown={onKey}
                      onMouseEnter={() => setHoveredTws(s)}
                      onMouseLeave={() => setHoveredTws(null)}
                      onMouseDown={() => setPressedTws(s)}
                      onTouchStart={() => setPressedTws(s)}
                      style={{
                        display: 'inline-flex',
                        flex: '0 0 auto',
                        alignItems: 'center',
                        gap: 6,
                        padding: '2px 8px',
                        borderRadius: 6,
                        border:
                          pressedTws === s
                            ? `1px solid var(--mantine-color-red-7)`
                            : hoveredTws === s
                              ? `1px solid var(--mantine-color-red-6)`
                              : `1px solid ${col}`,
                        cursor: 'pointer',
                        userSelect: 'none',
                        background:
                          pressedTws === s
                            ? 'var(--mantine-color-red-1)'
                            : hoveredTws === s
                              ? 'var(--mantine-color-red-0)'
                              : 'var(--mantine-color-body)',
                        color:
                          pressedTws === s || hoveredTws === s
                            ? 'var(--mantine-color-red-7)'
                            : undefined,
                        whiteSpace: 'nowrap',
                        transform: pressedTws === s ? 'scale(0.98)' : 'none',
                        transition:
                          'background-color 90ms ease, border-color 90ms ease, color 90ms ease, box-shadow 90ms ease, transform 60ms ease',
                      }}
                    >
                      <Box
                        style={{
                          width: 14,
                          height: 14,
                          borderRadius: 3,
                          background: col,
                          border: `1px solid ${BORDER_SUBTLE_COLOR}`,
                          flex: '0 0 auto',
                        }}
                      />
                      <Text
                        size="sm"
                        style={{ lineHeight: 1, color: TEXT_CONTRAST_COLOR }}
                      >
                        {tws} kts
                      </Text>
                    </Box>
                  );
                })
              ) : (
                <Text size="sm" c="dimmed" style={{ color: TEXT_MUTED_COLOR }}>
                  {t('polar.selectTwsHint', 'Select TWS lines to draw averaged curves.')}
                </Text>
              )}
            </Group>
          </Box>
        </Stack>

        <Box
          className="polar-chart-surface"
          ref={chartContainerReference}
          style={{
            width: '100%',
            height: '100%',
            position: 'relative',
            flex: 1,
            marginTop: 8,
            minWidth: MIN_CHART_WIDTH,
            minHeight: MIN_CHART_HEIGHT,
            overflow: 'hidden',
          }}
        >
          {canRenderChart ? (
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart
                data={combinedData}
                startAngle={rotate90 ? 90 : 180}
                endAngle={rotate90 ? -90 : 0}
                margin={chartMargins}
                outerRadius={outerRadius}
                cx={safeCx}
                cy={safeCy}
              >
                <PolarGrid
                  gridType="polygon"
                  polarRadius={gridRadii}
                  stroke={BORDER_SUBTLE_COLOR}
                  strokeOpacity={0.6}
                  radialLines
                />

                <PolarAngleAxis
                  type="number"
                  dataKey="angle"
                  domain={[0, 180]}
                  tickCount={angleTickCount}
                  stroke={BORDER_SUBTLE_COLOR}
                  tick={{ fontSize: angleTickFontPx, fill: TEXT_MUTED_COLOR }}
                  tickFormatter={(v: number) => `${v}°`}
                />

                <PolarRadiusAxis
                  angle={rotate90 ? 90 : 0}
                  domain={radialDomain}
                  tickCount={6}
                  tickFormatter={(v: number) => v.toFixed(0)}
                  stroke={BORDER_SUBTLE_COLOR}
                  tick={{ fill: TEXT_MUTED_COLOR }}
                  axisLine={false}
                  tickLine={false}
                />

                <Tooltip
                  isAnimationActive={false}
                  content={<PolarTooltipContent />}
                  wrapperStyle={{ outline: 'none' }}
                  cursor={{ stroke: 'var(--chart-3)', strokeDasharray: '4 4' }}
                />

                {/* 1) OVERLAY PRIMERO: así queda debajo */}
                {showPolarLines &&
                  Object.keys(overlay).map((twsString) => {
                    const tws = Number(twsString);
                    const color = pickColorByTws(
                      tws,
                      inferredBounds.tws.min,
                      inferredBounds.tws.max,
                    );
                    const field = `pv_${twsString}`;
                    const isHover = hoveredTws === twsString;
                    const dimmed = hoveredTws && !isHover;
                    return (
                      <Radar
                        key={`R-PL-${twsString}`}
                        name={`Polar ${tws} kts`}
                        dataKey={field}
                        stroke={color}
                        strokeWidth={isHover ? 2 : 1.5}
                        strokeOpacity={dimmed ? 0.35 : 0.55}
                        strokeDasharray="6 2"
                        fill="transparent"
                        isAnimationActive={false}
                        shape={<OpenRadarShape />}
                        // evitar capturar hover por encima de datos
                        style={{ pointerEvents: 'none' } as React.CSSProperties}
                      />
                    );
                  })}

                {/* 2) SERIES DE DATOS DESPUÉS: quedan encima y se ven */}
                {showData &&
                  selectedTwsLines.map((twsString) => {
                    const tws = Number(twsString);
                    const color = pickColorByTws(
                      tws,
                      inferredBounds.tws.min,
                      inferredBounds.tws.max,
                    );
                    const field = `v_${twsString}`;
                    const isHover = hoveredTws === twsString;
                    const dimmed = hoveredTws && !isHover;
                    const isPressed = pressedTws === twsString;
                    dbg('ShowData', { twsString, field, isHover, dimmed, isPressed });
                    const baseRadar = (
                      <Radar
                        key={`R-${twsString}-base`}
                        name={`${tws} kts`}
                        dataKey={field}
                        stroke={isPressed ? 'var(--mantine-color-red-6)' : color}
                        strokeWidth={isHover || isPressed ? 3 : 2.5} // un poco más grueso
                        strokeOpacity={dimmed ? 0.65 : 0.9}
                        fill={isPressed ? 'var(--mantine-color-red-6)' : color}
                        fillOpacity={isPressed ? 0.35 : isHover ? 0.24 : 0.14}
                        isAnimationActive={false}
                        shape={<OpenRadarShape />}
                        style={{ pointerEvents: 'none' } as React.CSSProperties}
                      />
                    );

                    if (!showDeviations) {
                      return baseRadar;
                    }

                    return [
                      baseRadar,
                      <Radar
                        key={`R-U-${twsString}`}
                        name={`${tws} kts +`}
                        dataKey={`vu_${twsString}`}
                        stroke={color}
                        strokeDasharray="3 3"
                        strokeWidth={1}
                        strokeOpacity={dimmed ? 0.35 : 0.6}
                        fill="transparent"
                        isAnimationActive={false}
                        shape={<OpenRadarShape />}
                      />,
                      <Radar
                        key={`R-L-${twsString}`}
                        name={`${tws} kts -`}
                        dataKey={`vl_${twsString}`}
                        stroke={color}
                        strokeDasharray="3 3"
                        strokeWidth={1}
                        strokeOpacity={dimmed ? 0.35 : 0.6}
                        fill="transparent"
                        isAnimationActive={false}
                        shape={<OpenRadarShape />}
                      />,
                    ];
                  })}
              </RadarChart>
            </ResponsiveContainer>
        ) : (
            <Text size="sm" c="dimmed" style={{ color: TEXT_MUTED_COLOR }}>
              {t('polar.preparing', 'Preparing chart…')}{' '}
              {DEBUG_POLAR ? '(debug on, see console)' : ''}
            </Text>
          )}
        </Box>
      </Card>
    </>
  );
}
